import { useNavigate } from "@tanstack/react-router";
import { IconAlertTriangle, IconArrowLeft } from "@tabler/icons-react";
import { Alert, Button, Group, PageHeader, Stack } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { FlujoCard } from "./FlujoCard";
import { ChatCard } from "./ChatCard";
import { AccionesAutorizadorCard } from "./AccionesAutorizadorCard";
import { ResumenSolicitudCard } from "./ResumenSolicitudCard";
import { useAuthStore } from "@/lib/auth-store";
import { tieneRolAutorizador } from "@/lib/roles";
import { enFlujoDeAutorizacion } from "../utils/estado-autorizacion";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useSolicitudPolling } from "@/features/solicitudes/hooks/use-solicitud-polling";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/autorizacion";

/**
 * Pantalla del paso "Autorización" del flujo de la solicitud.
 * Compone PageHeader + stepper + `<FlujoCard>` (timeline de niveles).
 * Polling cada 5s para reflejar autorizaciones del autorizador en tiempo real.
 */
export function AutorizacionPasoPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  useSolicitudPolling(id, 5_000);

  // El solicitante también puede tener rol Autorizador/MesaDeControl (típico
  // de coordinadores). Si lo tiene Y la solicitud está activa en el flujo de
  // autorización, mostramos las acciones del autorizador antes del chat para
  // que pueda resolver desde la misma pantalla.
  const user = useAuthStore((s) => s.user);
  const esAutorizadorOMesa =
    tieneRolAutorizador(user?.roles ?? []) || (user?.roles.includes("MesaDeControl") ?? false);
  const mostrarAcciones = esAutorizadorOMesa && enFlujoDeAutorizacion(sol.estado);

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Autorización"
        description={`Solicitud ${sol.folio} · ${sol.flujo?.cantidadNiveles ?? 0} nivel(es) de autorización`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: sol.folio, href: `/solicitudes/${id}` },
          { label: "Autorización" },
        ]}
      />
      <LayoutConStepper
        estado={sol.estado}
        subtipo={sol.subtipo}
        tipo={sol.tipo}
        paso="autorizacion"
        solicitudId={id}
        contabilizada={sol.enviadaAContabilizacion}
        fillHeight
      >
        {/* Columna de contenido en flex · el grid crece y el botón "Volver"
            queda al fondo, alineado con el contenido (como en los demás pasos). */}
        <Stack gap="md" className="h-full">
          <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-8">
              <Stack gap="md" className="h-full">
                <ResumenSolicitudCard sol={sol} />
                {sol.flujo ? (
                  <FlujoCard flujo={sol.flujo} fill />
                ) : (
                  <Alert variant="readonly" icon={<IconAlertTriangle size={16} />} className="flex-1">
                    La solicitud aún no tiene un flujo de autorización asignado.
                  </Alert>
                )}
                {mostrarAcciones ? <AccionesAutorizadorCard id={id} /> : null}
              </Stack>
            </div>
            <div className="col-span-12 md:col-span-4">
              <ChatCard solicitudId={id} fill />
            </div>
          </div>
          <Group justify="flex-start">
            <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate({ to: "/solicitudes" })}>
              Volver a solicitudes
            </Button>
          </Group>
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
